import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, FileText, ClipboardList, CalendarDays, MessageCircle, Sun, Moon } from 'lucide-react';
import { setPortalToken, portalApi } from './lib/api';
import { storeTokenInIDB, syncPendingUploads, requestBackgroundSync } from './lib/offline';
import { connectPortalSocket, disconnectPortalSocket } from './lib/socket';
import { UploadPage } from './pages/UploadPage';
import { HistoryPage } from './pages/HistoryPage';
import { RequestsPage } from './pages/RequestsPage';
import { DeadlinesPage } from './pages/DeadlinesPage';
import { MessagesPage } from './pages/MessagesPage';
import { InvalidLinkPage } from './pages/InvalidLinkPage';
import { useTheme } from './hooks/useTheme';

type Tab = 'upload' | 'history' | 'requests' | 'deadlines' | 'messages';

export function App() {
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('upload');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [clientName, setClientName] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  const { theme, toggleTheme } = useTheme();

  // Extract token from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) {
      setToken(urlToken);
      setPortalToken(urlToken);
      localStorage.setItem('portal-token', urlToken);
      storeTokenInIDB(urlToken);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else {
      // Check localStorage
      const stored = localStorage.getItem('portal-token');
      if (stored) {
        setToken(stored);
        setPortalToken(stored);
        storeTokenInIDB(stored);
      }
    }
  }, []);

  // Fetch portal context on mount (once token is available)
  useEffect(() => {
    if (!token) return;
    portalApi
      .getContext()
      .then((ctx: { clientName: string; contactName: string }) => {
        setClientName(ctx.clientName || '');
      })
      .catch(() => {
        // Context fetch failed — non-critical, keep going
      });
  }, [token]);

  // Connect Socket.io for real-time updates
  useEffect(() => {
    if (!token) return;
    const socket = connectPortalSocket(token);
    const events = [
      'document:uploaded', 'document:updated',
      'request:created', 'request:updated',
      'deadline:updated', 'deadlines:regenerated',
      'message:new', 'message:read',
      'task:created', 'task:updated',
    ];
    events.forEach((ev) => socket.on(ev, triggerRefresh));
    return () => {
      events.forEach((ev) => socket.off(ev, triggerRefresh));
      disconnectPortalSocket();
    };
  }, [token, triggerRefresh]);

  // Request Web Push permission and register subscription
  useEffect(() => {
    if (!token) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (Notification.permission === 'denied') return;

    const registerPush = async () => {
      try {
        const { publicKey } = await portalApi.getVapidKey();
        if (!publicKey) return;

        const registration = await navigator.serviceWorker.ready;

        // Check for existing subscription
        let sub = await registration.pushManager.getSubscription();
        if (!sub) {
          // Ask permission
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') return;

          // Subscribe
          const urlBase64 = Uint8Array.from(atob(publicKey.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));
          sub = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64,
          });
        }

        // Send subscription to server
        await portalApi.pushSubscribe(sub.toJSON());
      } catch {
        // Push registration is non-critical
      }
    };

    registerPush();
  }, [token]);

  // Online/offline detection + sync queued uploads on reconnect
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Try background sync first, fall back to manual sync
      requestBackgroundSync().catch(() => {
        syncPendingUploads().catch(() => {});
      });
    };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!token) {
    return <InvalidLinkPage />;
  }

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'upload', label: t('portal.uploadTitle'), icon: <Camera className="h-5 w-5" /> },
    { id: 'history', label: t('portal.history'), icon: <FileText className="h-5 w-5" /> },
    { id: 'requests', label: t('portal.requests'), icon: <ClipboardList className="h-5 w-5" /> },
    { id: 'deadlines', label: t('portal.deadlines'), icon: <CalendarDays className="h-5 w-5" /> },
    { id: 'messages', label: t('portal.messages'), icon: <MessageCircle className="h-5 w-5" /> },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="gradient-primary text-primary-foreground px-4 py-3 flex items-center justify-between flex-shrink-0 shadow-sm">
        <div className="flex items-center gap-2">
          <img src="/logo-192.png" alt="Taxistry" className="w-7 h-7 rounded-[10px]" />
          <span className="font-semibold text-sm">{clientName || 'Taxistry'}</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="text-primary-foreground/70 hover:text-primary-foreground transition-colors"
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <div className={`flex items-center gap-1.5 text-xs ${isOnline ? 'text-success' : 'text-destructive'}`}>
            <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-success shadow-[0_0_6px_rgba(34,197,94,0.4)]' : 'bg-destructive'}`} />
            {isOnline ? t('portal.online') : t('portal.offline')}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-auto">
        {activeTab === 'upload' && <UploadPage isOnline={isOnline} />}
        {activeTab === 'history' && <HistoryPage key={`history-${refreshKey}`} />}
        {activeTab === 'requests' && <RequestsPage key={`requests-${refreshKey}`} />}
        {activeTab === 'deadlines' && <DeadlinesPage key={`deadlines-${refreshKey}`} />}
        {activeTab === 'messages' && <MessagesPage key={`messages-${refreshKey}`} refreshKey={refreshKey} />}
      </main>

      {/* Bottom tabs */}
      <nav className="flex border-t border-border/40 bg-card/95 backdrop-blur-xl flex-shrink-0 safe-area-bottom">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex flex-col items-center gap-0.5 py-3 min-h-[52px] text-xs transition-colors relative rounded-[10px] ${
              activeTab === tab.id
                ? 'text-primary font-bold bg-brand-soft'
                : 'text-muted-foreground'
            }`}
          >
            {activeTab === tab.id ? (
              <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-primary" />
            ) : null}
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
